import { Building2, ClipboardList, Cog, Factory, MessageCircle, Phone, Sparkles, TrendingUp, User, Users, Wallet } from "lucide-react";
import { Badge } from "../ui/badge";
import { Progress } from "../ui/progress";
import { AnimatePresence, motion } from "framer-motion";

const STAGE_ORDER = [
  "greeting", "name", "company", "industry", "employees",
  "challenges", "software", "automation", "budget", "booking", "summary", "done",
];

const STAGE_LABEL = {
  greeting: "Greeting", name: "Name", company: "Company", industry: "Industry",
  employees: "Team size", challenges: "Challenges", software: "Existing software",
  automation: "Automation areas", budget: "Budget", booking: "Booking", summary: "Summary", done: "Complete",
};

const Row = ({ icon: Icon, label, value, testid }) => (
  <AnimatePresence mode="wait">
    {value ? (
      <motion.div
        key={String(value)}
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.25 }}
        className="flex items-start gap-3 py-2"
        data-testid={testid}
      >
        <div className="mt-0.5 h-7 w-7 shrink-0 rounded-lg bg-primary/10 text-primary grid place-items-center">
          <Icon className="w-3.5 h-3.5" />
        </div>
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
          <div className="text-sm font-medium leading-snug break-words">{value}</div>
        </div>
      </motion.div>
    ) : null}
  </AnimatePresence>
);

const scoreTone = (score) => {
  if (score >= 75) return { label: "Hot lead", cls: "bg-accent text-accent-foreground" };
  if (score >= 50) return { label: "Warm lead", cls: "bg-primary/15 text-primary" };
  if (score >= 25) return { label: "Cool lead", cls: "bg-muted text-muted-foreground" };
  return { label: "New lead", cls: "bg-muted text-muted-foreground" };
};

export const DiscoveryCard = ({ discovery, leadScore }) => {
  const anyData =
    discovery &&
    (discovery.name || discovery.company || discovery.industry || discovery.employees ||
      discovery.challenges || discovery.existing_software ||
      (discovery.automation_areas && discovery.automation_areas.length) || discovery.budget ||
      discovery.phone || discovery.email || discovery.preferred_time);

  const stage = discovery?.stage || "greeting";
  const stageIdx = Math.max(0, STAGE_ORDER.indexOf(stage));
  const stagePct = Math.round(((stageIdx + 1) / STAGE_ORDER.length) * 100);
  const tone = scoreTone(leadScore ?? 0);

  return (
    <div className="glass rounded-2xl p-5" data-testid="discovery-card">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <div className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 text-primary px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest">
            <Sparkles className="w-3 h-3" /> Consultation summary
          </div>
          <div className="mt-1 font-display text-lg font-semibold">What Ama has learnt</div>
        </div>
        <Badge className={`${tone.cls} shrink-0`} data-testid="discovery-score-badge">
          <TrendingUp className="w-3 h-3 mr-1" />
          {leadScore != null ? `${leadScore}/100` : "—"}
        </Badge>
      </div>

      <div className="mb-4">
        <div className="flex items-center justify-between text-[11px] text-muted-foreground mb-1">
          <span>Stage · {STAGE_LABEL[stage] || stage}</span>
          <span>{stagePct}%</span>
        </div>
        <Progress value={stagePct} data-testid="discovery-progress" />
      </div>

      {!anyData ? (
        <div className="text-sm text-muted-foreground text-center py-6 border border-dashed border-border/60 rounded-lg">
          Start chatting with Ama — the discovery summary will fill in here in real-time.
        </div>
      ) : (
        <div className="divide-y divide-border/60">
          <Row icon={User} label="Name" value={discovery.name} testid="discovery-name" />
          <Row icon={Building2} label="Company" value={discovery.company} testid="discovery-company" />
          <Row icon={Factory} label="Industry" value={discovery.industry} testid="discovery-industry" />
          <Row
            icon={Users}
            label="Employees"
            value={discovery.employees != null ? String(discovery.employees) : null}
            testid="discovery-employees"
          />
          <Row icon={ClipboardList} label="Current challenges" value={discovery.challenges} testid="discovery-challenges" />
          <Row icon={Cog} label="Existing software" value={discovery.existing_software} testid="discovery-software" />
          {discovery.automation_areas && discovery.automation_areas.length > 0 && (
            <div className="py-2 flex items-start gap-3" data-testid="discovery-areas">
              <div className="mt-0.5 h-7 w-7 shrink-0 rounded-lg bg-primary/10 text-primary grid place-items-center">
                <Sparkles className="w-3.5 h-3.5" />
              </div>
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Automation areas</div>
                <div className="mt-1 flex flex-wrap gap-1">
                  {discovery.automation_areas.map((a) => (
                    <Badge key={a} variant="secondary" className="capitalize text-[10px]">{a}</Badge>
                  ))}
                </div>
              </div>
            </div>
          )}
          <Row icon={Wallet} label="Budget" value={discovery.budget} testid="discovery-budget" />
          <Row icon={Phone} label="Phone" value={discovery.phone} testid="discovery-phone" />
          <Row icon={MessageCircle} label="Email" value={discovery.email} testid="discovery-email" />
          <Row icon={Sparkles} label="Preferred time" value={discovery.preferred_time} testid="discovery-time" />
        </div>
      )}
    </div>
  );
};

export default DiscoveryCard;
